import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { delay, filter, finalize, map, switchMap, tap } from 'rxjs';
import { ApexYAxis, NgApexchartsModule } from 'ng-apexcharts';
import {
  DateRangePickerComponent,
  IntegratorSelectComponent,
  TrackerPositionFilters,
  WhApexThemeService,
} from '@wheere-front/shared-ui';
import { MultiSelect } from 'primeng/multiselect';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs';
import {
  GRAPH_MAX_RAW_POINTS,
  SharedWebSocketService,
  subscribeWhenReady,
  WebSocketConnectionType,
  graphReduceLabel,
  telemetryApexToolbar,
  telemetryApexZoom,
} from '@wheere-front/shared-util';
import { DataPoint, GraphData } from '@wheere/api-gateway';
import { GatewaysV7Store } from '../data/gateways-v7.store';

interface DurationPreset {
  label: string;
  /** Subtract this much from "now" to get the start date. */
  amount: number;
  unit: 'minute' | 'hour' | 'day' | 'month';
  /** Default reduction (number of points). 0 = raw. */
  reduce: number;
}

const DURATIONS: DurationPreset[] = [
  { label: '1m', amount: 1, unit: 'minute', reduce: 0 },
  { label: '5m', amount: 5, unit: 'minute', reduce: 0 },
  { label: '15m', amount: 15, unit: 'minute', reduce: 0 },
  { label: '1h', amount: 1, unit: 'hour', reduce: 500 },
  { label: '6h', amount: 6, unit: 'hour', reduce: 500 },
  { label: '1D', amount: 1, unit: 'day', reduce: 500 },
  { label: '1W', amount: 7, unit: 'day', reduce: 500 },
  { label: '1M', amount: 1, unit: 'month', reduce: 500 },
];

const REDUCE_VALUES = [0, 100, 250, 500, 1000, 2500, 5000];

// Series palette: lead with the domain colour (yellow for gateways) then cycle through the
// rest of the Wheere palette so each curve in a multi-line graph reads distinctly.
const SERIES_COLORS = ['#ffc800', '#ED107D', '#5000CD', '#23D2CD', '#107DED', '#F05252'];

/**
 * V7 Gateway health-monitoring component — recreated from the inside `GatewayWeatherChart`
 * but hosted in `domain-gateways` so the integrator can use it without breaking the
 * `scope:integrator → scope:inside` boundary.
 *
 * <p>Pulls time-series from {@code GraphService.getGatewayGraphData(gatewayId, start, end, reduce)}
 * (the same {@code /api/gateway/gateways/{id}/graphs?startDate=&endDate=&reduce=} endpoint the
 * inside admin uses) on every duration change. Subscribes to
 * {@code /topic/gateway/{id}/graph} via the shared integrator WebSocket so the charts update
 * live as new points come in.
 *
 * <p>The user can pick which graphs to display (multi-select), choose a reduction factor, and
 * pick from preset durations (1m → 1M) — the {@code reduce} param is honored so we don't
 * fetch 100k raw points for a 1-month window.
 */
@Component({
  selector: 'lib-gateway-graphs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    IntegratorSelectComponent,
    DateRangePickerComponent,
    NgApexchartsModule,
    MultiSelect,
    DatePipe,
    DecimalPipe,
    TranslocoModule,
  ],
  template: `
    <div class="flex flex-col gap-4">
      <!-- Toolbar — Tailwind chips for durations (no PrimeNG pButton) so the
           active state can be themed and the height matches the selects. h-8
           baseline so the whole toolbar lines up on a single row on desktop. -->
      <div
        class="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 sm:gap-3 sm:p-3 dark:border-slate-700 dark:bg-slate-800">
        <div class="flex flex-wrap items-center gap-1">
          @for (d of durations; track d.label) {
            <button
              type="button"
              class="inline-flex h-8 items-center rounded-md border px-3 text-sm font-semibold transition-colors"
              [class.border-gray-300]="durationLabel() !== d.label"
              [class.text-gray-700]="durationLabel() !== d.label"
              [class.bg-white]="durationLabel() !== d.label"
              [class.hover:bg-gray-50]="durationLabel() !== d.label"
              [class.dark:border-slate-600]="durationLabel() !== d.label"
              [class.dark:bg-slate-700]="durationLabel() !== d.label"
              [class.dark:text-slate-200]="durationLabel() !== d.label"
              [class.dark:hover:bg-slate-600]="durationLabel() !== d.label"
              [class.bg-warmYellow-500]="durationLabel() === d.label"
              [class.border-warmYellow-500]="durationLabel() === d.label"
              [class.text-white]="durationLabel() === d.label"
              (click)="onDuration(d)">
              {{ d.label }}
            </button>
          }
        </div>

        <!-- Custom range: the shared date-range picker (same one as the position history), h-8
             like the duration chips. Applying it deselects the chips (durationLabel = 'custom');
             picking a chip takes over again. -->
        <lib-date-range-picker
          [initialFilters]="rangeFilters()"
          accent="warmYellow"
          [hideStatusFilter]="true"
          (filtersApplied)="onCustomRange($event)" />

        @if (graphData().length > 0) {
          <p-multiselect
            class="wh-h8-select"
            [options]="graphData()"
            [ngModel]="selectedGraphTitles()"
            (ngModelChange)="selectedGraphTitles.set($event)"
            optionLabel="title"
            optionValue="title"
            [placeholder]="'gatewayGraphs.toolbar.selectGraphs' | transloco"
            appendTo="body"
            size="small"
            [style]="{ minWidth: '14rem', maxWidth: '20rem', height: '2rem' }">
          </p-multiselect>
        }

        <lib-integrator-select
          width="10rem"
          size="sm"
          accent="warmYellow"
          [options]="reduceOptions()"
          [(ngModel)]="reduce" />

        <span class="ml-auto flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
          <span class="flex items-center gap-1.5">
            <span
              class="h-2 w-2 rounded-full"
              [class.bg-green-500]="wsConnected()"
              [class.bg-gray-400]="!wsConnected()"></span>
            {{ (wsConnected() ? 'gatewayGraphs.toolbar.live' : 'gatewayGraphs.toolbar.offline') | transloco }}
          </span>
          @switch (reduceLabel()) {
            @case ('reduced') {
              <span>{{
                'gatewayGraphs.toolbar.pointsDisplayed' | transloco: { pct: numberOfPointDisplay() | number }
              }}</span>
            }
            @case ('rawCapped') {
              <span>{{
                'gatewayGraphs.toolbar.rawCapped'
                  | transloco: { max: maxRawPoints | number, pct: numberOfPointDisplay() | number }
              }}</span>
            }
          }
          <span>{{ formatRange() }}</span>
        </span>
      </div>

      @if (loading()) {
        <div
          class="flex h-80 items-center justify-center rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <span class="animate-pulse text-gray-400 dark:text-slate-500">{{ 'gatewayGraphs.loading' | transloco }}</span>
        </div>
      } @else if (filteredGraphs().length === 0) {
        <div
          class="flex h-80 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-gray-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:text-slate-500">
          {{ 'gatewayGraphs.noData' | transloco }}
        </div>
      } @else {
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          @for (graph of filteredGraphs(); track graph.title) {
            <div class="rounded-xl border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
              <div class="mb-2 flex items-center justify-between">
                <div
                  class="wh-display truncate text-sm font-semibold text-gray-900 dark:text-slate-100"
                  [title]="graph.title">
                  {{ graph.title }}
                </div>
                <div class="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                  <span>{{ 'gatewayGraphs.yAxis.min' | transloco }}</span>
                  <input
                    type="number"
                    [placeholder]="'gatewayGraphs.yAxis.auto' | transloco"
                    [value]="getYAxis(graph.title!).min"
                    (change)="setYAxisMin(graph.title!, $any($event.target).value)"
                    class="focus:border-warmYellow-500 w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-900 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100" />
                  <span>{{ 'gatewayGraphs.yAxis.max' | transloco }}</span>
                  <input
                    type="number"
                    [placeholder]="'gatewayGraphs.yAxis.auto' | transloco"
                    [value]="getYAxis(graph.title!).max"
                    (change)="setYAxisMax(graph.title!, $any($event.target).value)"
                    class="focus:border-warmYellow-500 w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-900 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100" />
                </div>
              </div>

              <div class="mb-1 flex flex-wrap justify-center gap-3 text-xs text-gray-500 dark:text-slate-400">
                @for (legend of graph.legends ?? []; track legend; let i = $index) {
                  <span class="flex items-center gap-1.5">
                    <span class="h-2 w-2 rounded-full" [style.background]="colorFor(i)"></span>
                    <span>{{ legend }}</span>
                    <span
                      class="wh-display text-gray-900 dark:text-slate-100"
                      [title]="lastTimestamp(graph) | date: 'dd/MM HH:mm:ss'">
                      {{ lastValue(graph, legend) }}
                    </span>
                  </span>
                }
              </div>

              <apx-chart
                [series]="seriesFor(graph)"
                [chart]="chartOptions"
                [xaxis]="xaxisOptions"
                [yaxis]="yAxisFor(graph)"
                [stroke]="strokeOptions"
                [colors]="seriesColors"
                [legend]="legendOptions"
                [dataLabels]="dataLabelsOptions"
                [theme]="apex.theme()"
                [tooltip]="apex.tooltip()"
                [grid]="apex.grid()"
                [markers]="markersOptions">
              </apx-chart>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      /* Pin PrimeNG p-select / p-multiselect to h-8 so they line up with the
         duration chips. PrimeNG v18 puts the .p-select / .p-multiselect class
         on the host element directly, so the descendant selector
         (.wh-h8-select .p-select) doesn't match — we need the compound form
         (.wh-h8-select.p-select). Both forms listed for forward compat.
         !important overrides the inline height Aura sometimes sets. */
      :host ::ng-deep .wh-h8-select,
      :host ::ng-deep .wh-h8-select.p-select,
      :host ::ng-deep .wh-h8-select.p-multiselect,
      :host ::ng-deep .wh-h8-select .p-select,
      :host ::ng-deep .wh-h8-select .p-multiselect,
      :host ::ng-deep .wh-h8-select .p-multiselect-input {
        height: 2rem !important;
        min-height: 2rem !important;
        max-height: 2rem !important;
        display: flex !important;
        align-items: center !important;
        box-sizing: border-box !important;
      }
      :host ::ng-deep .wh-h8-select .p-select-label,
      :host ::ng-deep .wh-h8-select .p-multiselect-label,
      :host ::ng-deep .wh-h8-select .p-multiselect-label-container {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        line-height: 1.5rem !important;
        height: 100% !important;
        display: inline-flex !important;
        align-items: center !important;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      :host ::ng-deep .wh-h8-select {
        --p-form-field-padding-y: 0 !important;
        --p-form-field-sm-padding-y: 0 !important;
      }
    `,
  ],
})
export class GatewayGraphsComponent implements OnInit, OnDestroy {
  /**
   * Every apx-chart input other than `series` must keep a stable reference: ng-apexcharts
   * destroys and re-creates the chart when any of them changes (`hydrate()`), and inline object
   * literals in the template change on every change detection — one per live frame. That is
   * what closed the download menu the instant it opened, and redrew every chart every second.
   * Series and y-axis are memoised per graph object / per y-range.
   */
  protected readonly chartOptions = {
    type: 'line' as const,
    height: 200,
    background: 'transparent',
    animations: { enabled: false },
    toolbar: telemetryApexToolbar,
    zoom: telemetryApexZoom,
  };
  protected readonly xaxisOptions = {
    type: 'datetime' as const,
    labels: { style: { fontSize: '10px' }, datetimeUTC: false },
  };
  protected readonly strokeOptions = { curve: 'smooth' as const, width: 2 };
  protected readonly legendOptions = { show: false };
  protected readonly dataLabelsOptions = { enabled: false };
  protected readonly markersOptions = { size: 0 };
  readonly #seriesCache = new WeakMap<GraphData, ReturnType<typeof this.buildSeries>>();
  readonly #yAxisCache = new Map<string, ApexYAxis>();

  protected seriesFor(graph: GraphData): ReturnType<typeof this.buildSeries> {
    let s = this.#seriesCache.get(graph);
    if (!s) {
      s = this.buildSeries(graph);
      this.#seriesCache.set(graph, s);
    }
    return s;
  }

  protected yAxisFor(graph: GraphData): ApexYAxis {
    const cfg = this.getYAxis(graph.title!);
    const key = `${graph.title}|${cfg.min}|${cfg.max}`;
    let y = this.#yAxisCache.get(key);
    if (!y) {
      y = this.buildYAxis(graph);
      this.#yAxisCache.set(key, y);
    }
    return y;
  }
  gatewayId = input.required<string>();
  /**
   * STOMP connection the live graph subscribes on. The integrator opens `INTEGRATOR`; inside
   * opens its own per-domain connections and must pass one of them, or live never subscribes.
   */
  wsConnection = input<WebSocketConnectionType>(WebSocketConnectionType.INTEGRATOR);

  protected readonly durations = DURATIONS;
  protected readonly seriesColors = SERIES_COLORS;

  protected readonly graphData = signal<GraphData[]>([]);
  protected readonly loading = signal(false);
  protected readonly numberOfPointDisplay = signal<number>(100);

  /** Currently active duration preset. */
  protected readonly durationLabel = signal<string>('1h');
  /** Seed of the custom range picker — follows whatever range is currently displayed. */
  protected readonly rangeFilters = computed<TrackerPositionFilters>(() => ({
    from: this.startDate(),
    to: this.endDate(),
  }));
  protected readonly startDate = signal<Date>(dayjs().subtract(1, 'hour').toDate());
  protected readonly endDate = signal<Date>(new Date());
  protected readonly reduce = signal<number>(500);
  protected readonly maxRawPoints = GRAPH_MAX_RAW_POINTS;
  protected readonly reduceLabel = computed(() => graphReduceLabel(this.reduce(), this.numberOfPointDisplay()));

  /** Multi-select selection. Signal (not a plain array): filteredGraphs is a
   * computed, so the selection must be a tracked dependency — with a plain
   * property the checkboxes in the multiselect had no effect on the graphs. */
  selectedGraphTitles = signal<string[]>([]);

  /** YAxis overrides per graph title — `null` = auto. */
  protected readonly yAxisConfigs = signal<Record<string, { min: number | null; max: number | null }>>({});

  protected readonly wsConnected = signal(false);

  protected readonly filteredGraphs = computed<GraphData[]>(() => {
    const all = this.graphData();
    const selected = this.selectedGraphTitles();
    if (!selected.length) return all;
    const set = new Set(selected);
    return all.filter(g => set.has(g.title!));
  });

  protected readonly apex = inject(WhApexThemeService);

  #store = inject(GatewaysV7Store);
  #ws = inject(SharedWebSocketService);
  #injector = inject(Injector);
  #transloco = inject(TranslocoService);

  protected readonly reduceOptions = computed(() => {
    // Recompute when active language changes so labels stay translated.
    this.activeLang();
    return REDUCE_VALUES.map(n => ({
      label:
        n === 0
          ? this.#transloco.translate('gatewayGraphs.reduce.raw')
          : this.#transloco.translate('gatewayGraphs.reduce.points', { count: n }),
      value: n,
    }));
  });

  private readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });

  #stopLiveGraph: (() => void) | null = null;

  constructor() {
    // Auto-select all graphs on first load (mirrors inside).
    effect(
      () => {
        const list = this.graphData();
        if (list.length && !this.selectedGraphTitles().length) {
          this.selectedGraphTitles.set(list.map(g => g.title!));
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      // React to date / reduce / gatewayId changes — reload the graphs.
      const params = computed(() => ({
        id: this.gatewayId(),
        start: this.startDate(),
        end: this.endDate(),
        reduce: this.reduce(),
      }));
      toObservable(params)
        .pipe(
          filter(({ id, start, end }) => !!id && !!start && !!end),
          tap(() => this.loading.set(true)),
          switchMap(({ id, start, end, reduce }) => {
            return new Promise<{ graphs: GraphData[]; pct: number }>(resolve => {
              this.#store
                .loadGatewayGraphs(id, start, end, reduce)
                .then(resp => {
                  const graphs = (resp.graphDatas ?? []).slice().sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
                  resolve({ graphs, pct: resp.reducePourcentage ?? 100 });
                })
                .catch(() => resolve({ graphs: [], pct: 0 }));
            });
          }),
          delay(150),
          tap(({ graphs, pct }) => {
            this.graphData.set(graphs);
            this.numberOfPointDisplay.set(pct);
          }),
          finalize(() => this.loading.set(false)),
          takeUntilDestroyed(),
          map(() => undefined)
        )
        .subscribe({
          next: () => this.loading.set(false),
          error: () => this.loading.set(false),
        });
    });

    this.subscribeToLiveGraph();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromLiveGraph();
  }

  /** Custom range from the picker: no chip stays selected, the reduce factor is kept. */
  protected onCustomRange(f: TrackerPositionFilters): void {
    if (!f.from || !f.to) return;
    this.durationLabel.set('custom');
    this.startDate.set(f.from);
    this.endDate.set(f.to);
  }

  protected onDuration(d: DurationPreset): void {
    this.durationLabel.set(d.label);
    this.startDate.set(dayjs().subtract(d.amount, d.unit).toDate());
    this.endDate.set(new Date());
    this.reduce.set(d.reduce);
  }

  protected colorFor(i: number): string {
    return SERIES_COLORS[i % SERIES_COLORS.length];
  }

  protected buildSeries(graph: GraphData): { name: string; data: { x: number; y: number | null }[] }[] {
    return (graph.legends ?? []).map(legend => ({
      name: legend,
      data: (graph.data ?? []).map((dp: DataPoint) => ({
        x: new Date(dp.timestamp as unknown as string).getTime(),
        y: (dp.values?.[legend] as number | undefined) ?? null,
      })),
    }));
  }

  protected getYAxis(title: string): { min: number | null; max: number | null } {
    return this.yAxisConfigs()[title] ?? { min: null, max: null };
  }

  protected setYAxisMin(title: string, value: string): void {
    const next = { ...this.yAxisConfigs() };
    next[title] = { ...this.getYAxis(title), min: value === '' ? null : Number(value) };
    this.yAxisConfigs.set(next);
  }

  protected setYAxisMax(title: string, value: string): void {
    const next = { ...this.yAxisConfigs() };
    next[title] = { ...this.getYAxis(title), max: value === '' ? null : Number(value) };
    this.yAxisConfigs.set(next);
  }

  protected buildYAxis(graph: GraphData): ApexYAxis {
    const cfg = this.getYAxis(graph.title!);
    const yaxis: ApexYAxis = { labels: { style: { fontSize: '10px' } } };
    if (cfg.min !== null) yaxis.min = cfg.min;
    if (cfg.max !== null) yaxis.max = cfg.max;
    return yaxis;
  }

  protected lastValue(graph: GraphData, legend: string): string {
    const dp = (graph.data ?? []).at(-1);
    const v = dp?.values?.[legend];
    if (v == null) return '—';
    return typeof v === 'number' ? this.formatNumber(v) : String(v);
  }

  protected lastTimestamp(graph: GraphData): Date | null {
    const dp = (graph.data ?? []).at(-1);
    return dp?.timestamp ? new Date(dp.timestamp as unknown as string) : null;
  }

  protected formatRange(): string {
    return `${dayjs(this.startDate()).format('DD/MM HH:mm')} → ${dayjs(this.endDate()).format('DD/MM HH:mm')}`;
  }

  private formatNumber(v: number): string {
    if (Math.abs(v) >= 1000 || Number.isInteger(v)) return v.toFixed(0);
    if (Math.abs(v) >= 1) return v.toFixed(2);
    return v.toFixed(4);
  }

  /**
   * Subscribe to {@code /topic/gateway/{id}/graph} for live updates. The shared WS connection
   * is initialized at app startup but takes a moment to be ready — we poll for readiness then
   * subscribe once, and unsubscribe in {@link ngOnDestroy}.
   */
  private subscribeToLiveGraph(): void {
    this.unsubscribeFromLiveGraph();
    this.#stopLiveGraph = subscribeWhenReady(
      this.#ws,
      this.wsConnection(),
      `/topic/gateway/${this.gatewayId()}/graph`,
      raw => {
        const updated = this.parsePayload<GraphData>(raw);
        if (!updated) return;
        this.appendLiveGraph(updated);
      },
      { onReadyChange: ready => this.wsConnected.set(ready) }
    );
  }

  private unsubscribeFromLiveGraph(): void {
    this.#stopLiveGraph?.();
    this.#stopLiveGraph = null;
  }

  private parsePayload<T>(raw: unknown): T | null {
    if (raw == null) return null;
    if (typeof raw !== 'string') return raw as T;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  /**
   * Merge a live graph update into the existing series. If the title is already known we
   * append the new data points; otherwise we add the graph and auto-select it.
   */
  private appendLiveGraph(updated: GraphData): void {
    const current = this.graphData();
    const existing = current.find(g => g.title === updated.title);
    if (existing) {
      const merged: GraphData[] = current.map(g =>
        g.title === updated.title ? { ...g, data: [...(g.data ?? []), ...(updated.data ?? [])] } : g
      );
      this.graphData.set(merged);
    } else {
      this.graphData.set([...current, updated]);
      if (!this.selectedGraphTitles().includes(updated.title!)) {
        this.selectedGraphTitles.update(titles => [...titles, updated.title!]);
      }
    }
  }
}
